import type { KeyValue } from '@viewer/lib/ifcItemData'
import { explicarUnidade, type UnidadeDaPropriedade } from '@viewer/lib/unidades'
import EditableValue from './EditableValue'

interface Props {
  rows: KeyValue[]
  emptyMessage?: string
  /** When set, called per row to decide whether the value cell is
   *  editable. Return null to keep the row read-only; return a save
   *  callback to render the value via EditableValue. The IFCEdit
   *  module's PropertiesPanel integration uses this to make
   *  attributes and pset properties editable inline. */
  getOnSave?: (key: string) => ((next: string) => Promise<void>) | null
  /** Companion to `getOnSave`. When supplied AND the row is editable,
   *  the editor renders a `<select>` populated with these choices
   *  instead of a free-text input. Used for IFC enums
   *  (PredefinedType, ...). Return null/undefined for free-text. */
  getChoices?: (key: string) => readonly string[] | null | undefined
  /** A unidade de cada linha: a que a propriedade declara, ou a do
   *  projecto, ou a indicação de que falta. */
  unidadeDe?: (row: KeyValue) => UnidadeDaPropriedade | null
}

/** A unidade ao lado do valor. Da propriedade, em destaque; do
 *  projecto, discreta; em falta, a avisar. */
function MarcaDeUnidade({ u }: { u: UnidadeDaPropriedade }) {
  if (u.estado === 'sem-unidade' || u.estado === 'adimensional') return null
  const titulo = explicarUnidade(u)
  if (u.estado === 'ambigua') {
    return (
      <span
        title={titulo}
        className="ml-1 rounded border border-[var(--color-error-border)] px-1 text-[9px] text-[var(--color-error-text)]"
      >
        unidade ambígua
      </span>
    )
  }
  if (u.estado === 'em-falta') {
    return (
      <span
        title={titulo}
        className="ml-1 rounded border border-[var(--color-warn-border)] px-1 text-[9px] text-[var(--color-warn-text)]"
      >
        sem unidade
      </span>
    )
  }
  return (
    <span
      title={titulo}
      className={
        'ml-1 font-mono text-[10px] ' +
        (u.estado === 'propria' ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-muted)]')
      }
    >
      {u.unidade!.simbolo}
      {u.estado === 'propria' && <sup className="ml-px">*</sup>}
    </span>
  )
}

function KeyValueTable({ rows, emptyMessage, getOnSave, getChoices, unidadeDe }: Props) {
  if (rows.length === 0) {
    return (
      <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
        {emptyMessage ?? 'Sem dados.'}
      </div>
    )
  }
  return (
    <table className="w-full table-fixed border-collapse text-[11px]">
      <tbody>
        {rows.map((r, i) => {
          const onSave = getOnSave?.(r.key) ?? null
          const choices = onSave ? getChoices?.(r.key) ?? undefined : undefined
          return (
            <tr
              key={`${r.key}-${i}`}
              className="border-b border-[var(--color-border)]/40 last:border-b-0"
            >
              <td
                className="w-2/5 break-words py-1 pr-2 align-top text-[var(--color-text-muted)]"
                title={r.ifcType}
              >
                {r.key}
              </td>
              <td className="break-words py-1 align-top text-[var(--color-text)]">
                {onSave ? (
                  <EditableValue
                    value={r.value}
                    onSave={onSave}
                    choices={choices ?? undefined}
                  />
                ) : (
                  r.value
                )}
                {(() => {
                  const u = unidadeDe?.(r)
                  return u ? <MarcaDeUnidade u={u} /> : null
                })()}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default KeyValueTable
